import { lazy, Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { routes, routesKeys } from 'router';
import { BrowserRouter, HashRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { MathJaxContext } from 'better-react-mathjax';

import { useMatomo } from '@jonkoops/matomo-tracker-react';
import {
  GlobalFonts,
  GlobalDyslexicFonts,
} from '@maskott/tactileo.designsystem';

import i18n from 'translation/i18n';

import { themes } from 'themes/themes.js';
import { populateVoiceList } from 'utils/voiceAssistant.util';
import { useDeviceDetect } from 'hooks/useDeviceDetect';
import { useAccessibilityOptions } from 'hooks/useAccessibilityOptions';

import GlobalStyle from './GlobalStyle.style.js';
import { COMPONENT_SIZE } from 'constants';
import ScormWrapper from "utils/scormWrapper.util";
import {
  updateActiveVoiceAssistantState,
  updateSpeedVoice,
  updateVoiceOptions,
  updateSelectedVoice,
} from 'store/voiceAssistant.slice';
import config from 'config.json';

const APP_NAME = 'Player Tactileo';
/**
 * Entry component
 * @returns component Player on run route, component Result on result route and component Go on all other routes
 */
function App() {
  const PrivateRoute = lazy(() => import('components/auth/PrivateRoute'));
  const template = useSelector((state) => state.activitySession.template);
  const activitySession = useSelector((state) => state.activitySession);
  const dispatch = useDispatch();
  const activityCultureName = activitySession?.contextCultureName;
  const activityExpirationCode = activitySession?.activity?.expirationCode;

  const { isMobile } = useDeviceDetect();
  const {
    dyslexicState,
    highContrastState,
    invertColorsState,
    vocalAssistantState,
    voiceAssistantVoiceState,
    voiceAssistantSpeedState,
    fontFlag,
    fontState,
  } = useAccessibilityOptions();
  const [title, setTitle] = useState(APP_NAME);
  const { trackPageView, enableLinkTracking } = useMatomo();

  const theme =
    template && themes[template.id] && themes[template.id][template.variant]
      ? themes[template.id][template.variant]
      : themes.tactileo.default;
  theme.colors.lightness = highContrastState.flag
    ? theme.contrastedLightness
    : theme.defaultLightness;

  theme.componentSize = fontFlag
    ? fontFlag
    : isMobile
      ? COMPONENT_SIZE.XSMALL
      : theme.defaultComponentSize
        ? theme.defaultComponentSize
        : COMPONENT_SIZE.MEDIUM;

  if (
    process.env.NODE_ENV === 'production' &&
    process.env.REACT_APP_MODE_VAR === 'online'
  ) {
    enableLinkTracking();
    useEffect(() => {
      if (trackPageView) {
        trackPageView();
      }
    }, [trackPageView]);
  }

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    setTitle(`${activitySession?.activity?.title} - ${APP_NAME}`);
  }, [activitySession?.activity?.title]);

  useEffect(() => {
    dispatch(
      updateActiveVoiceAssistantState({
        isActive: vocalAssistantState.flag,
      }),
    );
  }, [vocalAssistantState.flag]);

  useEffect(() => {
    if (voiceAssistantSpeedState.optionFromMenu)
      dispatch(updateSpeedVoice({ speedVoice: voiceAssistantSpeedState.flag }));
  }, [voiceAssistantSpeedState.optionFromMenu, voiceAssistantSpeedState.flag]);

  useEffect(() => {
    if (voiceAssistantVoiceState.optionFromMenu)
      dispatch(
        updateSelectedVoice({ selectedVoice: voiceAssistantVoiceState.flag }),
      );
  }, [voiceAssistantVoiceState.optionFromMenu, voiceAssistantVoiceState.flag]);

  useEffect(() => {
    theme.colors.lightness = highContrastState.optionFromMenu
      ? theme.contrastedLightness
      : theme.defaultLightness;
  }, [highContrastState.optionFromMenu, theme]);

  useEffect(() => {
    if (fontState) theme.componentSize = fontState;
  }, [fontState, theme]);

  useEffect(() => {
    const lang = theme?.defaultLang
      ? theme?.defaultLang
      : theme?.languageSuffix
        ? `${activityCultureName}-${theme.languageSuffix}`
        : activityCultureName;

    i18n.changeLanguage(lang);
    dispatch(updateVoiceOptions({ voiceOptions: populateVoiceList(lang) }));
  }, [activityCultureName, theme]);


  useEffect(() => {
    ScormWrapper.init(activityExpirationCode);
    const handleExit = () => {
      ScormWrapper.commit();  // Save SCORM progress before closing
      ScormWrapper.finish();  
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        ScormWrapper.commit();
      }
    };
    const handlePageHide = (event) => {
      if (event.persisted) return; // Ignore when navigating back via browser cache
      handleExit();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      handleExit();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [activityExpirationCode]);



  const Router =
    process.env.REACT_APP_MODE_VAR === 'standalone'
      ? HashRouter
      : BrowserRouter;

  return (
    <MathJaxContext version={3} config={config.MATHJAX}>
      <ThemeProvider theme={theme}>
        {dyslexicState.flag ? <GlobalDyslexicFonts /> : <GlobalFonts />}
        <GlobalStyle
          invertColorsFlag={invertColorsState.flag}
          vocalAssistant={vocalAssistantState.flag}
          isMobile={isMobile}
          size={theme.componentSize}
        />
        <Router>
          <Routes>
            {routesKeys.map((key) => (
              <Route
                key={key}
                path={routes[key].path}
                element={
                  <Suspense fallback={<div id="preloader"></div>}>
                    <PrivateRoute component={routes[key].component} />
                  </Suspense>
                }
              />
            ))}
          </Routes>
        </Router>
      </ThemeProvider>
    </MathJaxContext>
  );
}

export default App;
